<template>
  <section v-if="!hidePostgradoComparison" class="projection-comparison" aria-label="Comparación con el año anterior" :aria-busy="pending ? 'true' : 'false'">
    <header class="comparison-heading">
      <h2>Comparación con el año anterior</h2>
      <span class="context-chip">{{ segmentLabel }} · {{ period || 'Sin período' }}</span>
    </header>
    <p v-if="pending" class="coverage-note" role="status">Cargando comparación…</p>
    <p v-else-if="visibleError" class="data-warning" role="alert">{{ visibleError }}</p>
    <p v-else-if="!group" class="coverage-note" role="status">No hay comparación disponible para este período y segmento.</p>
    <template v-else>
      <p class="table-note">Corte: {{ dateLabel(comparison.coverage_through) }} · Mismo tramo de cada año; avance sobre su propia meta fija.</p>
      <div class="table-responsive" tabindex="0" role="region" aria-label="Tabla de comparación desplazable">
        <table class="projection-table">
          <thead><tr><th scope="col">Período</th><th scope="col">Matrículas</th><th scope="col">Meta</th><th scope="col">Avance</th><th scope="col" title="Avance actual menos anterior, en puntos porcentuales">Diferencia (pp)</th></tr></thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <th scope="row">{{ row.label }} · {{ row.period }}</th>
              <td>{{ number(row.count) }}</td><td>{{ isNumber(row.target) ? number(row.target) : 'Sin meta' }}</td>
              <td>{{ percentage(row.pct) }}</td><td>{{ row.key === 'current' ? points(difference) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="group.previous_target_source === 'confirmed_historical'" class="table-note">Meta de {{ comparison.previous_period }}: total histórico confirmado manualmente. No modifica las metas comerciales ni el plan original.</p>
      <p v-if="group.provisional || missingTargets.length || zeroTargets.length" class="comparison-footnote" role="status">
        <span v-if="group.provisional">Comparación provisional: cobertura sin confirmar o meta no disponible. </span>
        <span v-if="missingTargets.length">Falta meta para {{ missingTargets.join(' y ') }}. </span>
        <span v-if="zeroTargets.length">Meta cero para {{ zeroTargets.join(' y ') }}. </span>
        <span>—: no disponible.</span>
      </p>
    </template>
  </section>
</template>

<script>
import axios from 'axios';

const isNumber = value => typeof value === 'number' && Number.isFinite(value);

export default {
  name: 'ProjectionComparison',
  props: {
    period: { type: String, default: null },
    segment: { type: String, default: 'advance' },
    revision: { type: [String, Number], default: null },
  },
  data() {
    return { payload: null, loadedKey: null, stateKey: null, loading: false, error: '', requestId: 0, disposed: false };
  },
  computed: {
    hidePostgradoComparison() {
      return this.segment === 'postgrado' && !!this.comparison
        && (!this.group || !isNumber(this.group.previous_target) || this.group.previous_target <= 0);
    },
    // A single batched watcher deduplicates period + Excel revision changes.
    requestKey() { return JSON.stringify([this.period, this.revision]); },
    pending() { return !!this.period && (this.stateKey !== this.requestKey || this.loading); },
    visibleError() { return this.stateKey === this.requestKey ? this.error : ''; },
    comparison() { return this.loadedKey === this.requestKey && this.payload && this.payload.period === this.period ? this.payload : null; },
    group() { return this.comparison && this.comparison.groups && this.comparison.groups[this.segment]; },
    segmentLabel() { return ({ advance: 'Advance', postgrado: 'Magíster/Posgrado', diplomados: 'Diplomados' })[this.segment] || this.segment; },
    rows() {
      if (!this.group) return [];
      return ['current', 'previous'].map(key => {
        const count = this.group[`${key}_count`];
        const target = this.group[`${key}_target`];
        return { key, label: key === 'current' ? 'Actual' : 'Anterior',
          period: key === 'current' ? this.comparison.period : this.comparison.previous_period,
          count, target, pct: isNumber(target) && target > 0 && isNumber(count) ? this.group[`${key}_pct`] : null };
      });
    },
    missingTargets() { return this.rows.filter(row => !isNumber(row.target)).map(row => row.period); },
    zeroTargets() { return this.rows.filter(row => row.target === 0).map(row => row.period); },
    difference() { return this.rows.length === 2 && this.rows.every(row => isNumber(row.pct)) ? this.group.difference_pp : null; },
  },
  watch: {
    requestKey: { immediate: true, handler() { this.load(); } },
  },
  beforeDestroy() { this.disposed = true; this.requestId++; },
  methods: {
    isNumber,
    number(value, digits = 0) { return isNumber(value) ? value.toLocaleString('es-CL', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '—'; },
    percentage(value) { return isNumber(value) ? `${this.number(value, 1)} %` : '—'; },
    points(value) { return isNumber(value) ? `${value > 0 ? '+' : ''}${this.number(value, 1)} pp` : '—'; },
    dateLabel(value) { return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split('-').reverse().join('/') : 'No informada'; },
    async load() {
      if (this.disposed) return;
      const id = ++this.requestId;
      const key = this.requestKey;
      const period = this.period;
      this.stateKey = key;
      this.payload = null;
      this.loadedKey = null;
      this.error = '';
      this.loading = !!period;
      if (!period) return;
      // Check the live key too: props may change before the queued watcher runs.
      const active = () => !this.disposed && id === this.requestId && key === this.requestKey;
      try {
        const { data } = await axios.get('/api/dashboard/proyeccion/comparacion', { params: { period } });
        if (!active()) return;
        if (!data || data.period !== period || !data.groups) throw new Error('Invalid comparison response');
        this.payload = data;
        this.loadedKey = key;
      } catch (error) {
        if (active()) this.error = 'No se pudo cargar la comparación. Vuelve a actualizar la vista o selecciona el período nuevamente.';
      } finally {
        if (active()) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.projection-comparison { color: #24324a; background: #fff; border: 1px solid #e5eaf2; border-radius: 14px; box-shadow: 0 3px 12px #23365604; padding: 24px; margin-top: 22px; min-width: 0; }
.comparison-heading { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; }
h2 { font-size: 18px; margin: 0; color: #24324a; }
.context-chip { background: #eef1f8; color: #53627b; padding: 7px 11px; border-radius: 8px; font-size: 12px; }
.table-note { font-size: 12px; color: #65738b; line-height: 1.7; margin: 12px 0; }
.comparison-footnote { font-size: 11px; color: #77531b; line-height: 1.5; margin: 10px 0 0; }
.coverage-note { padding: 12px 16px; background: #f0f4fa; border-left: 3px solid #7d89ad; border-radius: 6px; font-size: 12px; color: #53627b; }
.data-warning { background: #fff5dd; border: 1px solid #f3d79b; border-radius: 10px; padding: 13px 18px; margin: 18px 0; color: #77531b; line-height: 1.6; font-size: 13px; }
.table-responsive { overflow-x: auto; }
.table-responsive:focus-visible { outline: 3px solid #9694ef; outline-offset: 3px; }
.projection-table { width: 100%; font-size: 12px; border-collapse: collapse; }
caption { font-size: 12px; color: #65738b; }
th, td { padding: 12px; border-bottom: 1px solid #edf0f6; text-align: right; }
th { background: #f7f9fc; color: #63718a; }
th:first-child { text-align: left; }
td { white-space: nowrap; }
@media(max-width:600px) { .projection-comparison { padding: 15px; }.projection-table { min-width: 560px; } }
</style>